import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import EditableNote from "@/components/EditableNote";
import { PrioBadge } from "@/components/PrioridadToggle";
import { CosturaSelect } from "@/components/CosturaSelect";
import { FabricadosDelDia } from "@/components/FabricadosDelDia";
import { CategoriasAsignadas } from "@/components/CategoriasAsignadas";
import { toast } from "sonner";
import { Scissors, Search, Settings2 } from "lucide-react";

const CosturasConfig = ({ onSaved }) => {
  const [cats, setCats] = useState(null);
  useEffect(() => { api.get("/costuras/config").then(({ data }) => setCats(data.categorias || [])); }, []);
  if (cats === null) return null;
  const save = async () => {
    try { await api.put("/costuras/config", { categorias: cats }); toast.success("Categorías de Costuras guardadas"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="border border-zinc-200 rounded-md bg-white p-4" data-testid="costuras-config">
      <CategoriasAsignadas value={cats} onChange={setCats} title="Categorías que pasan a Costuras" hint="Los productos de estas categorías aparecen automáticamente en Costuras." />
      <Button onClick={save} className="mt-3 h-9 bg-[#002FA7] hover:bg-[#00227A]" data-testid="costuras-config-save">Guardar</Button>
    </div>
  );
};

export default function Costuras() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState("activos");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [showCfg, setShowCfg] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/costuras", { params: { vista: "activos" } }); setRows(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    if (view !== "activos") return undefined;
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 30000);
    return () => clearInterval(t);
  }, [view]);

  const s = q.trim().toLowerCase();
  const filtered = s ? rows.filter((r) => [r.numero, r.cliente, r.producto, r.color, r.tela].some((v) => (v || "").toLowerCase().includes(s))) : rows;
  const updNotas = (oid, v) => setRows((p) => p.map((x) => (x.order_id === oid ? { ...x, notas_hoja: v } : x)));

  return (
    <div className="space-y-6" data-testid="costuras-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Producción</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3"><Scissors className="w-8 h-8 text-[#DB2777]" /> Costuras</h1>
          <p className="text-zinc-500 mt-2 text-sm">Productos que necesitan costura, recibidos automáticamente desde la Hoja de Pedidos.</p>
        </div>
        {isAdmin && <Button variant="outline" className="gap-1" onClick={() => setShowCfg((v) => !v)} data-testid="costuras-config-toggle"><Settings2 className="w-4 h-4" /> Categorías</Button>}
      </header>
      {showCfg && <CosturasConfig onSaved={() => { setShowCfg(false); load(); }} />}

      <div className="flex gap-2 border-b border-zinc-200">
        {[{ v: "activos", l: `Activos (${rows.length})` }, { v: "fabricados", l: "Fabricados del día" }].map((t) => (
          <button key={t.v} onClick={() => setView(t.v)} data-testid={`costuras-view-${t.v}`}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${view === t.v ? "border-[#DB2777] text-[#DB2777] font-semibold" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>{t.l}</button>
        ))}
      </div>

      {view === "fabricados" ? <FabricadosDelDia fuente="costuras" canEdit /> : (
        <>
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pedido, cliente, producto, color o tela..." className="pl-9" data-testid="costuras-search" />
          </div>
          <div className="overflow-x-auto border border-zinc-200 rounded-md bg-white">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>{["Pedido", "Cliente", "Producto", "Estilo", "Tamaño", "Color", "Tela", "Cant.", "Notas", "Estado"].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.length === 0 && <tr><td colSpan={10} className="text-center text-zinc-400 py-10">{rows.length === 0 ? "No hay productos pendientes de costura (revise las categorías configuradas)." : "Sin resultados."}</td></tr>}
                {filtered.map((r) => (
                  <tr key={`${r.order_id}-${r.key}`} className={`align-top ${r.prioridad ? "bg-red-100 hover:bg-red-200" : "hover:bg-zinc-50"}`} data-testid={`costura-row-${r.numero}`}>
                    <td className="px-3 py-2 font-mono text-xs font-semibold whitespace-nowrap">{r.numero}<div><PrioBadge show={r.prioridad} /></div>{r.fecha_entrega && <div className="text-[10px] text-orange-700 font-sans font-normal">Entrega: {r.fecha_entrega}</div>}</td>
                    <td className="px-3 py-2 text-xs">{r.cliente}</td>
                    <td className="px-3 py-2 text-xs font-semibold">{r.producto}</td>
                    <td className="px-3 py-2 text-xs">{r.estilo}</td>
                    <td className="px-3 py-2 text-xs">{r.tamano}</td>
                    <td className="px-3 py-2 text-xs">{r.color}</td>
                    <td className="px-3 py-2 text-xs">{r.tela}</td>
                    <td className="px-3 py-2 text-xs font-bold">{r.cantidad}</td>
                    <td className="px-3 py-2 min-w-[180px]">{r.nota_pedido && <div className="text-[11px] bg-yellow-50 border border-yellow-200 rounded p-1.5 mb-1">Pedido: {r.nota_pedido}</div>}<EditableNote orderId={r.order_id} modulo="notas_cos" initial={r.notas_hoja} onSaved={(v) => updNotas(r.order_id, v)} /></td>
                    <td className="px-3 py-2"><CosturaSelect row={r} canEdit onChanged={load} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
